// The Job view (docs/job-view-brief.md, 2026-10-22; docs/job-view-build-notes.md is the contract). Answers: which
// step is this job on and what does it need of me? The frame is the job bar (name, base, scope and item counts,
// price), the step strip (the job's workflow steps that open a tab, in workflow order, done · now) with the tools at its
// right (Items, Labor, Materials, Subs, Files, Details; Cut list and Variance once their step is reachable), and the
// body: the active tab. The header's workflow position and the tab strip are one progression; there is no second strip.
//
// The address: /jobs/<id>?tab=<key>&item=<item id>&view=<view id>&build=<build id>&addons=<a,b>&lit=<what>&back=<tab>&show=fronts
// (the build notes say what each means). Every tab reads the job priced under `build` (the base when absent) with the
// add-ons in `addons` on; the Scope, Price and Proposal tabs price every build themselves from the job as entered.

import { useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { STEP, stepsForJob, type ResolvedItem } from '@zerogap/engine';
import { useCrumb } from '../../frame/Frame';
import { LIBRARY_TABLES } from '@zerogap/db';
import { deleteRow, insertRow, toast, useRereadOnReturn, useRows, type Row } from '../../lib/api';
import { nextNumber, prefixFor } from '../../lib/create';
import { hours } from '../../lib/format';
import { baseBuild, plainPricing, pricingOptions, useJobData, useResolved, type JobData, type Resolved } from '../../lib/job';
import { useSession } from '../../lib/session';
import { Btn, Empty, Field, Loading, Modal, Money, Panel } from '../../ui/kit';
import { CadCheck } from './CadCheck';
import { ChangeOrderDues } from './ChangeOrders';
import { CutList } from './CutList';
import { Details } from './Details';
import { JobViews } from './Files';
import { GridEntry } from './GridEntry';
import { LaborTab, MaterialsTab } from './Breakdown';
import { Lock } from './Lock';
import { MaterialVariance } from './MaterialVariance';
import { Price } from './Price';
import { Proposal } from './Proposal';
import { ScopeTab } from './Scope';
import { Setup } from './Setup';
import { Subs } from './Subs';
import { Suggestions } from './Suggestions';
import { Walk } from './Walk';
import { VarianceList } from '../Shop';
import '../../styles/jobview.css';

/** A step's tab, or a tool. The step tabs come from workflow_step.tab (data); the tools from the job_view_tools setting. */
export type TabKey = string;

/** What a jump lit on landing: step:<ResolvedStep.key>, line:<ResolvedBom.key>, input:<key>, route:<job route line id>, bom:<job bom line id>, standard:<key>, sub:<sub line id>. */
export interface Lit {
  kind: 'step' | 'line' | 'input' | 'route' | 'bom' | 'standard' | 'sub' | 'price';
  key: string;
}

export interface JobCtx {
  jobId: string;
  data: JobData;
  /** The job priced under `build` with `addOnsOn` on: what every tab reads unless it prices every build itself. */
  r: Resolved;
  /** The job as entered, every build priced from it (Scope, Price, Proposal). */
  asEntered: Resolved;
  /** The Build the tabs price under (the base unless the address picks another); null when the job has none. */
  build: Row | null;
  /** The add-ons on, by id. */
  addOnsOn: string[];
  /** The workflow: every step row, the steps this job goes through (retired ones out), and the step it is on. */
  steps: Row[];
  mySteps: Row[];
  step: Row | null;
  /** The address: the open item, the View shown, what a jump lit and where it came from. */
  tab: TabKey;
  itemId: string | null;
  viewId: string | null;
  showFronts: boolean;
  lit: Lit | null;
  back: TabKey | null;
  /** Placing asked for from another tab (the Views table's Place marks): Items built reads it once and takes it off the address. */
  place: boolean;
  /** One change to the address: a tab, an item, a View, a build, the add-ons, what is lit, placing. A key set to null is removed. */
  go: (change: Partial<Record<'tab' | 'item' | 'view' | 'build' | 'addons' | 'lit' | 'back' | 'show' | 'place', string | null>>) => void;
  /** The old selection shape, kept for the pieces that still use it (Add many, + Item): one item, in the address. */
  selected: string[];
  select: (ids: string[]) => void;
  locked: boolean;
}

/** The one jump control's action: open a line's home with it lit, and the way back to the tab left (section 5). */
export function jumpTo(ctx: JobCtx, to: { tab: TabKey; item?: string | null; lit: string; back: TabKey }) {
  ctx.go({ tab: to.tab, item: to.item ?? null, lit: to.lit, back: to.back, show: null });
}

export function parseLit(s: string | null): Lit | null {
  if (!s) return null;
  const at = s.indexOf(':');
  if (at < 0) return null;
  const kind = s.slice(0, at) as Lit['kind'];
  if (!['step', 'line', 'input', 'route', 'bom', 'standard', 'sub', 'price'].includes(kind)) return null;
  return { kind, key: s.slice(at + 1) };
}

interface Tool {
  key: string;
  label: string;
  from_step?: string | null;
}

export function Workspace() {
  const { jobId = '' } = useParams();
  const [params, setParams] = useSearchParams();
  const s = useSession();
  const data = useJobData(jobId);
  const r = useResolved(data);
  // a row signed or a recipe changed in the editor's tab shows here on the way back
  useRereadOnReturn(LIBRARY_TABLES);
  const stepRows = useRows('workflow_step', { order: ['sort'] });
  const jobSteps = useRows('job_step', { eq: { job_id: jobId }, enabled: !!jobId });
  const [adding, setAdding] = useState(false);
  const [grid, setGrid] = useState(false);

  const buildId = params.get('build') ?? '';
  const addOnKey = params.get('addons') ?? '';
  const base = baseBuild(data.builds);
  const pricedBuild = data.builds.find((b) => b.id === buildId) ?? base;
  const addOnsOn = addOnKey.split(',').filter(Boolean);
  const priced: Resolved = useMemo(() => {
    const opts = pricingOptions(pricedBuild, data.addOns.filter((a) => addOnsOn.includes(a.id)));
    if (!r.res || plainPricing(opts)) return r;
    return { ...r, res: r.resolveWith(opts) ?? r.res };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [r, data.addOns, pricedBuild, addOnKey]);

  const go: JobCtx['go'] = (change) => {
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        for (const [k, v] of Object.entries(change)) {
          if (v === null || v === undefined || v === '') next.delete(k);
          else if (k === 'build' && v === base?.id) next.delete(k);
          else next.set(k, v);
        }
        return next;
      },
      { replace: true },
    );
  };

  const title = `${data.opportunity?.name ?? '…'}`;
  useCrumb([{ label: 'Jobs', to: '/jobs' }, { label: title, to: data.opportunity ? `/opportunities/${data.opportunity.id}` : undefined }, { label: data.job?.scope_name ?? '…' }]);

  const labels = s.setting<Record<string, string>>('option_labels', {});
  const tools = s.setting<Tool[]>('job_view_tools', []);
  const proposalHidden = s.setting<boolean>('proposal_tab_hidden', false);
  const scopeRows = useRows('scope_row', { eq: { opportunity_id: data.job?.opportunity_id }, enabled: !!data.job?.opportunity_id });
  // the Suggestions tool is back on the strip (Spencer, 2026-10-26: what an AI employee files must reach him); its count is what still waits
  const waitingSuggestions = useRows('suggestion', { eq: { job_id: jobId, status: 'waiting' } });
  // a For Spencer item waits on him the same way (Iris, 2026-10-29): the tab counts both
  const waitingHandoffs = useRows('handoff', { eq: { job_id: jobId, status: 'waiting' } });
  const waitingCount = waitingSuggestions.rows.length + waitingHandoffs.rows.length;

  if (data.loading || r.loading || stepRows.loading) return <Loading what="Loading the job" />;
  if (!data.job) return <Empty>This job no longer exists.</Empty>;
  if (!r.res) return <Empty>The job could not be resolved: {r.error ?? 'the library is not loaded yet.'}</Empty>;

  const res = priced.res ?? r.res;
  const locked = !!data.job.locked_at;
  // the steps this job goes through: a step may belong only to jobs with a certain attribute (the design lane is the lead's)
  const attrs = { ...data.job, design_lane: data.opportunity?.design_lane ?? null };
  const mySteps = stepsForJob(stepRows.rows as any[], attrs) as Row[];
  const step = stepRows.rows.find((x) => x.id === data.job!.stage_id) ?? null;
  const stepSort = step ? Number(step.sort) : -1;
  const won = stepRows.rows.find((x) => x.key === STEP.won);
  const doneStep = (x: Row) => jobSteps.rows.some((js) => js.workflow_step_id === x.id && js.actual_date) || (stepSort >= 0 && Number(x.sort) < stepSort);
  // the progression: the job's steps that open a tab; one after Won appears once the job is at Won or past it
  const progression = mySteps.filter((x) => x.tab && (!won || Number(x.sort) < Number(won.sort) || stepSort >= Number(won.sort)) && !(x.tab === 'proposal' && proposalHidden));
  // the tools: always visible, or once their step is reachable
  const shownTools = tools.filter((t) => {
    if (!t.from_step) return true;
    const from = stepRows.rows.find((x) => x.key === t.from_step);
    return !from || stepSort >= Number(from.sort);
  });
  const can = (k: string) => s.can(k);
  const allowed = (key: TabKey): boolean => {
    if (key === 'price' || key === 'subs') return can('screen.price') || can('screen.subs');
    if (key === 'setup') return can('screen.job_setup');
    if (key === 'scope') return can('screen.pass1') || can('screen.jobs');
    if (key === 'proposal') return can('screen.proposal');
    if (key === 'files') return can('screen.files');
    if (key === 'variance') return can('screen.variance');
    if (key === 'details') return true;
    if (key === 'suggestions') return can('screen.jobs') || can('screen.item');
    return can('screen.item') || can('screen.jobs');
  };
  const stepTabs = progression.filter((x) => allowed(x.tab));
  const toolTabs = shownTools.filter((t) => allowed(t.key));
  const wanted = params.get('tab') ?? '';
  const known = new Set([...stepTabs.map((x) => x.tab as string), ...toolTabs.map((t) => t.key)]);
  // with no tab asked for, the job opens on the step it is on (its tab, or the nearest progression tab before it)
  const stepTab = step?.tab && known.has(step.tab) ? (step.tab as string) : [...stepTabs].reverse().find((x) => Number(x.sort) <= stepSort)?.tab ?? stepTabs[0]?.tab ?? 'item';
  const tab: TabKey = known.has(wanted) ? wanted : stepTab;

  const itemId = params.get('item');
  const ctx: JobCtx = {
    jobId, data, r: priced, asEntered: r, build: pricedBuild, addOnsOn, steps: stepRows.rows, mySteps, step,
    tab, itemId, viewId: params.get('view'), showFronts: params.get('show') === 'fronts', lit: parseLit(params.get('lit')), back: params.get('back'), place: params.get('place') === '1',
    go, selected: itemId ? [itemId] : [], select: (ids) => go({ item: ids[0] ?? null }), locked,
  };
  const topItems = res.items.filter((i) => !i.parent_item_id).length;
  const walkMode = tab === 'quick_check' ? 'quick' : tab === 'engineering_check' || tab === 'engineering_review' ? 'walk' : 'items';

  return (
    <div className={`jv${tab === 'proposal' ? ' noprint' : ''}`}>
      <div className="jobbar">
        <span className="jn">
          {title} · {data.job.scope_name}
          {locked ? ' 🔒' : ''}
        </span>
        {base && <span className="chip">Base: {base.name}</span>}
        {pricedBuild && base && pricedBuild.id !== base.id && <span className="chip">Pricing: {pricedBuild.name}</span>}
        <span className="mut">
          {scopeRows.rows.length} scope {scopeRows.rows.length === 1 ? 'row' : 'rows'} · {topItems} {topItems === 1 ? 'item' : 'items'}
          {res.totals.unverified_items > 0 ? ` · ${res.totals.unverified_items} unverified` : ''}
        </span>
        <ChangeOrderDues jobId={jobId} />
        {can('see_prices') && (
          <span className="price">
            <Money cents={res.totals.price_cents} />
            <small>{hours(res.totals.labor_minutes)} · {/standards/i.test(res.standards_version_name) ? res.standards_version_name : `standards ${res.standards_version_name}`}</small>
          </span>
        )}
      </div>
      <div className="steps" role="tablist" aria-label="Job steps">
        {stepTabs.map((x) => (
          <button key={x.id} type="button" role="tab" aria-selected={tab === x.tab} className={`${tab === x.tab ? 'now' : doneStep(x) ? 'dn' : ''}${x.gate ? ' gate' : ''}`} title={x.id === step?.id ? `${x.name} · the job is here` : doneStep(x) ? `${x.name} · done` : x.name} onClick={() => go({ tab: x.tab, lit: null, back: null })}>
            {x.name}
          </button>
        ))}
        <span className="gap" />
        <div className="tools">
          {toolTabs.map((t) => (
            <button key={t.key} type="button" role="tab" aria-selected={tab === t.key} className={tab === t.key ? 'now' : ''} onClick={() => go({ tab: t.key, lit: null, back: null })}>
              {t.key === 'builds' ? `${labels.build ?? 'Builds'}` : t.key === 'suggestions' && waitingCount > 0 ? `${t.label} · ${waitingCount}` : t.label}
            </button>
          ))}
        </div>
      </div>
      <div className="body">
        {(tab === 'item' || tab === 'quick_check' || tab === 'engineering_check' || tab === 'engineering_review') && <Walk ctx={ctx} mode={walkMode} onAdd={() => setAdding(true)} onGrid={() => setGrid(true)} />}
        {tab === 'scope' && <ScopeTab ctx={ctx} />}
        {tab === 'setup' && <Setup ctx={ctx} />}
        {tab === 'price' && <Price ctx={ctx} />}
        {tab === 'proposal' && <Proposal ctx={{ ...ctx, r: r, build: null }} />}
        {tab === 'lock' && <Lock ctx={ctx} />}
        {tab === 'labor' && <LaborTab ctx={ctx} />}
        {tab === 'materials' && <MaterialsTab ctx={ctx} />}
        {tab === 'subs' && <Subs ctx={ctx} />}
        {tab === 'cut' && <CutList ctx={ctx} />}
        {tab === 'files' && (
          <div className="stack" style={{ padding: 14 }}>
            <JobViews ctx={ctx} />
            {can('screen.item') && <CadCheck ctx={ctx} />}
          </div>
        )}
        {tab === 'variance' && (
          <div className="stack" style={{ padding: 14 }}>
            <VarianceList jobId={jobId} />
            <MaterialVariance jobId={jobId} />
          </div>
        )}
        {tab === 'details' && <Details ctx={ctx} />}
        {tab === 'suggestions' && <Suggestions ctx={ctx} />}
      </div>
      {adding && <AddItem ctx={ctx} onClose={() => setAdding(false)} />}
      {grid && <GridEntry ctx={ctx} onClose={() => setGrid(false)} />}
    </div>
  );
}

// ---------- + Item ----------

export function AddItem({ ctx, onClose }: { ctx: JobCtx; onClose: () => void }) {
  const { data, r, jobId, select } = ctx;
  const lib = r.lib!;
  const offered = useRows('job_type_catalog_item', { eq: { job_type_id: data.job?.job_type_id }, enabled: !!data.job?.job_type_id });
  const [all, setAll] = useState(false);
  const [catId, setCatId] = useState<string>('');
  const [name, setName] = useState('');
  const [number, setNumber] = useState('');
  // on a job with rooms, a new item lands in the room of the item open (if any), else in none
  const [roomId, setRoomId] = useState<string>(() => (ctx.itemId ? r.res?.items.find((i) => i.id === ctx.itemId)?.room_id ?? '' : ''));
  const [addOnId, setAddOnId] = useState<string>('');
  // a retired item is offered to no new job
  const items = Object.values(lib.catalog).filter((c) => !c.retired).sort((a, b) => (a.family ?? '').localeCompare(b.family ?? '') || a.name.localeCompare(b.name));
  const limited = offered.rows.length > 0 && !all ? items.filter((c) => offered.rows.some((o) => o.catalog_item_id === c.id)) : items;
  const verifiedWord = (c: { id: string; verified: boolean }): string => {
    const rs = lib.catalog[c.id]?.recipes ?? [];
    const v = rs.filter((x) => x.verified).length;
    if (rs.length === 0 || v === rs.length) return c.verified || rs.length > 0 ? '' : ' (unverified)';
    if (v === 0) return ' (unverified)';
    return ` (${rs.filter((x) => !x.verified).map((x) => x.name).join(', ')} unverified)`;
  };
  const custom = catId === 'custom';
  const cat = lib.catalog[catId];
  const suggested = nextNumber(data.items.map((i) => i.number), custom ? 'X' : cat ? prefixFor(cat.name) : 'I');
  useEffect(() => {
    if (ctx.locked) onClose();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const create = async () => {
    const row: Row = {
      job_id: jobId,
      number: number.trim() || suggested,
      name: name.trim() || (custom ? 'Custom item' : cat?.name ?? ''),
      sort: Math.max(0, ...data.items.map((i) => i.sort ?? 0)) + 1,
    };
    if (custom) row.is_custom = true;
    else row.catalog_item_id = catId;
    if (roomId) row.room_id = roomId;
    if (addOnId) row.add_on_id = addOnId;
    const made = await insertRow('job_item', row);
    select([made.id]);
    onClose();
  };

  return (
    <Modal title="+ Item" onClose={onClose}>
      <div className="form">
        <Field label="What is it" wide>
          <select value={catId} onChange={(e) => setCatId(e.target.value)} autoFocus>
            <option value="">Pick a catalog item…</option>
            {limited.map((c) => (
              <option key={c.id} value={c.id}>
                {c.family ? `${c.family} · ` : ''}
                {c.name}
                {verifiedWord(c)}
              </option>
            ))}
            <option value="custom">Custom — build it inside this job</option>
          </select>
        </Field>
        <Field label="Item number">
          <input type="text" value={number} placeholder={suggested} onChange={(e) => setNumber(e.target.value)} />
        </Field>
        <Field label="Name">
          <input type="text" value={name} placeholder={custom ? '' : cat?.name ?? ''} onChange={(e) => setName(e.target.value)} />
        </Field>
        {data.rooms.length > 0 && (
          <Field label="Room">
            <select value={roomId} onChange={(e) => setRoomId(e.target.value)}>
              <option value="">No room · the job's standards</option>
              {data.rooms.map((x) => (
                <option key={x.id} value={x.id}>
                  {x.name}
                </option>
              ))}
            </select>
          </Field>
        )}
        {data.addOns.length > 0 && (
          <Field label="Priced with">
            <select value={addOnId} onChange={(e) => setAddOnId(e.target.value)}>
              <option value="">Base</option>
              {data.addOns.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name} · add-on
                </option>
              ))}
            </select>
          </Field>
        )}
      </div>
      {offered.rows.length > 0 && (
        <label className="check" style={{ margin: '10px 0' }}>
          <input type="checkbox" checked={all} onChange={(e) => setAll(e.target.checked)} /> Show every catalog item, not only the ones this job type offers
        </label>
      )}
      <div style={{ marginTop: 10 }}>
        <Btn primary disabled={!catId} onClick={() => void create()}>
          Add
        </Btn>
      </div>
    </Modal>
  );
}

export async function removeItem(ctx: JobCtx, item: ResolvedItem) {
  if (!item.stored) return;
  if (!window.confirm(`Remove ${item.number} ${item.name} from this job?`)) return;
  ctx.select([]);
  await deleteRow('job_item', item.id);
  toast(`${item.number} removed.`);
}

export function toggle(list: string[], id: string): string[] {
  return list.includes(id) ? list.filter((x) => x !== id) : [...list, id];
}
